import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from "html5-qrcode"
import { Modal } from "bootstrap"

/**
 * QR Scanner Modal Controller
 * 
 * Контроллер для сканирования QR-кодов моделей в модальном окне.
 * Используется для:
 * 1. Сканирования QR-кода при создании новой модели в форме
 * 2. Сканирования QR-кода при выборе существующей модели из поиска
 * 
 * После успешного сканирования:
 * - Проверяет уникальность кода в текущем соревновании
 * - Подставляет код в целевое поле или вызывает callback
 */
export default class extends Controller {
  static targets = ["modal", "scannerContainer", "errorMessage", "closeBtn", "cameraSelect", "cameraSelectionBlock"]
  static values = {
    competitionId: Number,
    validateUrl: String,
    // Переводы
    cameraPermissionDenied: { type: String, default: "Доступ к камере запрещён. Разрешите доступ к камере в настройках браузера." },
    noCameraFound: { type: String, default: "Камера не найдена на устройстве." },
    cameraNotSupported: { type: String, default: "Камера не поддерживается в этом браузере." },
    scannerError: { type: String, default: "Ошибка сканера. Попробуйте еще раз." },
    codeAlreadyAssigned: { type: String, default: "Такой QR код уже присвоен модели %{name}" }
  }

  connect() {
    this.scanner = null
    this.isScanning = false
    this.pendingCallback = null
    this.targetInput = null
    this.userId = null
    this.userName = null
    this.availableCameras = []
    this.selectedCameraId = null
    this.loadSavedCamera()
  }

  disconnect() {
    this.stopScanner()
  }

  /**
   * Открывает модальное окно сканера
   * 
   * @param {Object} options - параметры
   * @param {HTMLInputElement} options.targetInput - поле для подстановки кода
   * @param {Function} options.callback - callback после успешного сканирования
   * @param {Number} options.userId - ID пользователя (для режима выбора модели)
   * @param {String} options.userName - имя пользователя (для режима выбора модели)
   */
  async open(options = {}) {
    this.targetInput = options.targetInput || null
    this.pendingCallback = options.callback || null
    this.userId = options.userId || null
    this.userName = options.userName || null

    // Скрываем ошибки
    this.hideError()

    // Загружаем список камер
    await this.loadAvailableCameras()

    // Показываем модальное окно
    const modal = new Modal(this.modalTarget)
    modal.show()

    // Ждём открытия модального окна, затем запускаем сканер и управляем z-index backdrop
    this.modalTarget.addEventListener('shown.bs.modal', () => {
      this.startScanner()
      this.adjustBackdropZIndex()
    }, { once: true })

    // Останавливаем сканер при закрытии модального окна
    this.modalTarget.addEventListener('hidden.bs.modal', () => {
      this.stopScanner()
    }, { once: true })
  }

  /**
   * Закрывает модальное окно сканера
   */
  close() {
    this.stopScanner()
    const modalInstance = Modal.getInstance(this.modalTarget)
    if (modalInstance) {
      modalInstance.hide()
    }
  }

  /**
   * Загружает список доступных камер и отображает селект
   */
  async loadAvailableCameras() {
    try {
      this.availableCameras = await Html5Qrcode.getCameras()
      this.updateCameraSelect()
    } catch (error) {
      console.error("Failed to get cameras:", error)
      // Если ошибка при получении камер, сразу запускаем сканер без выбора
      this.hideCameraSelection()
    }
  }

  /**
   * Обновляет селект камер на основе доступных устройств
   */
  updateCameraSelect() {
    if (!this.hasCameraSelectTarget || this.availableCameras.length === 0) {
      this.hideCameraSelection()
      return
    }

    // Показываем блок выбора камеры
    this.cameraSelectionBlockTarget.style.display = 'block'

    // Очищаем опции кроме первой (placeholder)
    const select = this.cameraSelectTarget
    while (select.options.length > 1) {
      select.remove(1)
    }

    // Добавляем камеры в селект
    this.availableCameras.forEach((camera, index) => {
      const option = document.createElement('option')
      option.value = camera.id
      option.textContent = camera.label || `Camera ${index + 1}`
      select.appendChild(option)
    })

    // Если есть сохраненная камера и она все еще доступна, выбираем её
    if (this.selectedCameraId && this.availableCameras.some(cam => cam.id === this.selectedCameraId)) {
      select.value = this.selectedCameraId
    } else if (this.availableCameras.length > 0) {
      // Иначе выбираем первую камеру
      select.value = this.availableCameras[0].id
      this.selectedCameraId = this.availableCameras[0].id
    }
  }

  /**
   * Скрывает блок выбора камеры (если нет камер)
   */
  hideCameraSelection() {
    if (this.hasCameraSelectionBlockTarget) {
      this.cameraSelectionBlockTarget.style.display = 'none'
    }
  }

  /**
   * Обработчик изменения выбранной камеры
   */
  async handleCameraChange(event) {
    const selectedId = event.target.value
    if (!selectedId) return

    this.selectedCameraId = selectedId
    this.saveCameraId()

    // Перезапускаем сканер с новой камерой
    await this.stopScanner()
    await this.startScanner()
  }

  /**
   * Сохраняет ID выбранной камеры в localStorage
   */
  saveCameraId() {
    if (this.selectedCameraId) {
      localStorage.setItem('selectedQrScannerCameraId', this.selectedCameraId)
    }
  }

  /**
   * Загружает сохраненную камеру из localStorage
   */
  loadSavedCamera() {
    const saved = localStorage.getItem('selectedQrScannerCameraId')
    if (saved) {
      this.selectedCameraId = saved
    }
  }

  /**
   * Запускает сканер камеры
   */
  async startScanner() {
    if (this.isScanning) return

    try {
      // Очищаем контейнер перед инициализацией
      this.scannerContainerTarget.innerHTML = ''

      const scannerId = `qr-scanner-${Date.now()}`
      const scannerDiv = document.createElement('div')
      scannerDiv.id = scannerId
      this.scannerContainerTarget.appendChild(scannerDiv)

      this.scanner = new Html5Qrcode(scannerId)
      
      const config = {
        fps: 10,
        // qrbox: { width: 250, height: 250 },
        // aspectRatio: 1.0
      }

      // Определяем какую камеру использовать
      const cameraConstraints = this.selectedCameraId 
        ? { deviceId: { exact: this.selectedCameraId } }
        : { facingMode: "environment" }

      await this.scanner.start(
        cameraConstraints,
        config,
        this.onScanSuccess.bind(this),
        this.onScanFailure.bind(this)
      )

      this.isScanning = true
    } catch (error) {
      console.error("Failed to start QR scanner:", error)
      this.handleScannerError(error)
    }
  }

  /**
   * Останавливает сканер камеры
   */
  async stopScanner() {
    if (this.scanner && this.isScanning) {
      try {
        await this.scanner.stop()
        this.scanner = null
        this.isScanning = false
      } catch (error) {
        console.error("Failed to stop QR scanner:", error)
      }
    }
    
    // Очищаем контейнер
    if (this.hasScannerContainerTarget) {
      this.scannerContainerTarget.innerHTML = ''
    }
  }

  /**
   * Обработчик успешного сканирования
   */
  async onScanSuccess(decodedText, decodedResult) {
    console.log(`QR Code scanned: ${decodedText}`, decodedResult)

    // Останавливаем сканер
    await this.stopScanner()

    // Проверяем уникальность кода
    const validation = await this.validateCode(decodedText)
    
    if (!validation.valid) {
      this.showError(validation.error)
      // Перезапускаем сканер для повторной попытки
      setTimeout(() => this.startScanner(), 2000)
      return
    }

    // Код валиден - закрываем модальное окно
    this.close()

    // Если есть целевое поле - подставляем код
    if (this.targetInput) {
      this.targetInput.value = decodedText
      // Диспатчим событие change для обновления формы
      this.targetInput.dispatchEvent(new Event('change', { bubbles: true }))
    }

    // Вызываем callback если есть
    if (this.pendingCallback) {
      this.pendingCallback({
        code: decodedText,
        userId: this.userId,
        userName: this.userName
      })
    }

    // Диспатчим кастомное событие
    this.dispatch("scanned", { 
      detail: { 
        code: decodedText,
        userId: this.userId,
        userName: this.userName
      }
    })
  }

  /**
   * Обработчик неудачного сканирования (продолжаем сканировать)
   */
  onScanFailure(error) {
    // Игнорируем ошибки сканирования, они происходят постоянно пока QR код не найден
  }

  /**
   * Проверяет уникальность кода в текущем соревновании
   */
  async validateCode(code) {
    if (!this.hasValidateUrlValue || !this.validateUrlValue) {
      // Если URL не задан, считаем код валидным
      return { valid: true }
    }

    try {
      const url = new URL(this.validateUrlValue, window.location.origin)
      url.searchParams.set('code', code)

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      const data = await response.json()

      if (data.valid) {
        return { valid: true }
      } else {
        // Формируем сообщение об ошибке с подстановкой имени
        const errorMessage = this.codeAlreadyAssignedValue.replace('%{name}', data.model_name || '')
        return { valid: false, error: errorMessage }
      }
    } catch (error) {
      console.error("Error validating code:", error)
      // При ошибке сети разрешаем использовать код (проверка будет на сервере)
      return { valid: true }
    }
  }

  /**
   * Обработка ошибок сканера
   */
  handleScannerError(error) {
    let message = this.scannerErrorValue

    if (error.name === 'NotAllowedError') {
      message = this.cameraPermissionDeniedValue
    } else if (error.name === 'NotFoundError') {
      message = this.noCameraFoundValue
    } else if (error.name === 'NotSupportedError') {
      message = this.cameraNotSupportedValue
    }

    this.showError(message)
  }

  /**
   * Показывает сообщение об ошибке
   */
  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('d-none')
    } else {
      alert(message)
    }
  }

  /**
   * Скрывает сообщение об ошибке
   */
  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('d-none')
      this.errorMessageTarget.textContent = ''
    }
  }

  /**
   * Устанавливает высокий z-index для backdrop этого модала
   * чтобы он отображался поверх других открытых модалов
   */
  adjustBackdropZIndex() {
    // Bootstrap создаёт backdrop после модала, нужно немного подождать
    setTimeout(() => {
      // Находим все backdrops и устанавливаем высокий z-index для последнего (нашего)
      const backdrops = document.querySelectorAll('.modal-backdrop')
      if (backdrops.length > 0) {
        const lastBackdrop = backdrops[backdrops.length - 1]
        lastBackdrop.classList.add('qr-scanner-backdrop')
      }
    }, 10)
  }
}
